import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { Edit, LucideAngularModule, Trash, CheckCircle, XCircle, Clock } from 'lucide-angular';
import { EventService } from '@app/events/shared/event.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { DatePipe } from '@angular/common';
import { ToastService } from '@app/shared/toast/toast.service';

@Component({
  selector: 'app-event-reminders',
  templateUrl: './reminders.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [
    ButtonComponent,
    LucideAngularModule,
    TextareaComponent,
    TextInputComponent,
    DatePickerComponent,
    TimePickerComponent,
    ModalComponent,
    DatePipe,
  ],
  standalone: true,
})
export class RemindersComponent implements OnInit {
  @Input() public event!: EventModel;
  @Output() toggleOpen = new EventEmitter<any>();

  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;
  protected readonly CheckCircleIcon = CheckCircle;
  protected readonly XCircleIcon = XCircle;
  protected readonly ClockIcon = Clock;

  // Method to get status badge class
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
      case 'pending':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/30 dark:text-gray-300 dark:border-gray-700';
    }
  }

  // Method to get status text
  getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'failed':
        return 'Failed';
      case 'pending':
      default:
        return 'Pending';
    }
  }

  // Method to get status icon
  getStatusIcon(status: string): any {
    switch (status) {
      case 'sent':
        return this.CheckCircleIcon;
      case 'failed':
        return this.XCircleIcon;
      case 'pending':
      default:
        return this.ClockIcon;
    }
  }

  protected form = new FormGroup({
    title: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(1000)],
    }),
    description: new FormControl('', {
      validators: [Validators.maxLength(1000)],
    }),
    due_date: new FormControl('', [Validators.required]),
    due_time: new FormControl('', [Validators.required]),
  });

  protected validationErrors: string[] = [];

  protected processing = false;
  protected loading = false;
  protected loadError: string | null = null;

  protected isDeleteModalOpen = false;
  protected deleteRemindersId: number | null = null;
  protected deleteReminderTitle: string = '';

  protected isEditing = false;
  protected editingReminderId: number | null = null;

  constructor(
    private eventService: EventService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Set default time to 8 AM
    this.form.patchValue({
      due_time: '08:00:00'
    });

    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    this.loadReminders();
  }

  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value as any;
    data.remindable_type = 'event';
    data.remindable_id = this.event.id;

    this.processing = true;

    this.eventService.createEventReminder(data).subscribe({
      next: (response) => {
        this.event.reminders.push(response.data);
        this.validationErrors = [];
        this.form.reset();
        this.form.patchValue({
          due_time: '08:00:00'
        });
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.processing = false;
        this.toastService.success('Reminder created successfully');
        this.cdr.detectChanges(); // Force change detection
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error('Failed to create reminder', 'Please try again');
      },
    });
  }

  getFormValidationErrors(form: FormGroup): string[] {
    const errors: string[] = [];
    Object.keys(form.controls).forEach((key) => {
      const controlErrors = form.get(key)?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((errorKey) => {
          errors.push(`${key} has error: ${errorKey}`);
        });
      }
    });
    return errors;
  }

  deleteReminder(id: any) {
    const reminder = this.event.reminders.find(r => r.id === id);
    this.isDeleteModalOpen = true;
    this.deleteRemindersId = id;
    this.deleteReminderTitle = reminder?.title || 'this reminder';
  }

  getDeleteConfirmationMessage(): string {
    return `Are you sure you want to delete the reminder "${this.deleteReminderTitle}"? This action cannot be undone.`;
  }

  cancelDelete() {
    this.isDeleteModalOpen = false;
    this.deleteRemindersId = null;
    this.deleteReminderTitle = '';
  }

  isFormEmpty(): boolean {
    const formValue = this.form.value;
    return !formValue.title || !formValue.due_date || !formValue.due_time;
  }

  confirmDelete() {
    if (this.deleteRemindersId) {
      this.eventService.deleteEventReminder(this.deleteRemindersId).subscribe({
        next: () => {
          this.event.reminders = this.event.reminders.filter((reminder) => reminder.id !== this.deleteRemindersId);
          this.isDeleteModalOpen = false;
          this.deleteRemindersId = null;
          this.deleteReminderTitle = '';
          this.toastService.success('Reminder deleted successfully');
          this.cdr.detectChanges(); // Force change detection
        },
        error: (res) => {
          console.error('Error deleting reminder:', res);
          this.toastService.error('Failed to delete reminder', 'Please try again');
        },
      });
    }
  }

  cancel() {
    this.validationErrors = [];
    this.form?.reset();
    this.form.patchValue({
      due_time: '08:00:00'
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.processing = false;
    this.toggleOpen.emit();
  }

  private loadReminders(): void {
    if (!this.event?.id) {
      return;
    }

    this.loading = true;
    this.loadError = null;
    this.event.reminders = this.event.reminders ?? [];

    this.eventService.getEventReminders(this.event.id).subscribe({
      next: (response) => {
        this.event.reminders = response.data ?? [];
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = 'Failed to load reminders.';
        console.error('Error loading reminders:', error);
      },
    });
  }
}
